import { useCarouselPrevNextButtons } from '@/hooks';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Flex,
  FlexProps,
  useIsBreakpoint
} from '@blockanalitica/ui';
import { EmblaCarouselType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { ReactNode, useCallback } from 'react';
import styled, { css } from 'styled-components';

interface EmblaSlideProps {
  $slideWidth: string;
}

interface PaginationButtonProps extends FlexProps {
  $disabled: boolean;
}

export interface CarouselProps {
  children: ReactNode[];
  showArrows?: boolean;
  delay?: number;
}

const Embla = styled.div`
  overflow: hidden;
`;

const EmblaContainer = styled.div`
  display: flex;
  touch-action: pan-y pinch-zoom;
  margin-left: calc(${(props) => props.theme.space['s']} * -1);
`;

const EmblaSlide = styled.div<EmblaSlideProps>`
  display: flex;
  transform: translate3d(0, 0, 0);

  min-width: 0;
  box-sizing: border-box;

  ${(props) => css`
    flex: 0 0 ${props.$slideWidth};
    padding-left: ${props.theme.space['s']};
  `}
`;

const PaginationButton = styled(Flex)<PaginationButtonProps>`
  cursor: pointer;

  ${(props) => css`
    &:hover {
      color: ${props.theme.colors.linkHover};
    }
  `}

  ${(props) =>
    props.$disabled &&
    `
    color: ${props.theme.colors.muted};
    cursor: default;
  `}
`;

export default function Carousel({
  delay = 0,
  showArrows = true,
  children,
  ...rest
}: CarouselProps) {
  const isMobile = useIsBreakpoint(0);
  const isBreakpoint2 = useIsBreakpoint(2);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { align: 'start', loop: true },
    delay ? [Autoplay({ stopOnInteraction: false, delay: delay })] : []
  );

  const onNavButtonClick = useCallback((emblaApi: EmblaCarouselType) => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;

    const resetOrStop =
      autoplay.options.stopOnInteraction === false
        ? autoplay.reset
        : autoplay.stop;

    resetOrStop();
  }, []);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  } = useCarouselPrevNextButtons(emblaApi, onNavButtonClick);

  let slideWidth = '33.3%';
  if (children.length === 1) {
    slideWidth = '100%';
  }
  if (children.length === 2 || isBreakpoint2) {
    slideWidth = '50%';
  }

  if (isMobile) {
    slideWidth = '100%';
  }

  return (
    <Flex flexDirection="column" gap="xs">
      <Embla ref={emblaRef} {...rest}>
        <EmblaContainer>
          {children?.map((child, index) => (
            <EmblaSlide key={index} $slideWidth={slideWidth}>
              {child}
            </EmblaSlide>
          ))}
        </EmblaContainer>
      </Embla>
      {showArrows && (!prevBtnDisabled || !nextBtnDisabled) ? (
        <Flex justifyContent="center" gap="xs">
          <PaginationButton
            onClick={onPrevButtonClick}
            $disabled={prevBtnDisabled}>
            <ArrowLeftIcon size="1" />
          </PaginationButton>
          <PaginationButton
            onClick={onNextButtonClick}
            $disabled={nextBtnDisabled}>
            <ArrowRightIcon size="1" />
          </PaginationButton>
        </Flex>
      ) : null}
    </Flex>
  );
}
